import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, FormikProvider, useFormik } from 'formik';
import { toast } from 'react-toastify';

import { ISponsor } from 'src/@types/leaderboard';
import { ModalProps } from 'src/layouts/AdminLayout';
import sponsorConroller from 'src/services/leaderboard/sponsorConroller';
import Button from 'src/ui/Button';
import Input from 'src/ui/Input';
import Modal from 'src/ui/Modal';

import { FORM_VALIDATION_SCHEMA, INITIAL_VALUES } from './constants';

import s from './style.module.scss';

import LogoImage from 'src/assets/images/btcs-logo.png';

export type GiveawayModalProps = {
  sponsorId: string;
  editingReward?: any | null;
  onSuccessCreated?: (raffle: ISponsor) => void;
} & ModalProps;

interface IFormState {
  name: string;
  image: string;
  value: number;
}

const clearFormState = {
  name: '',
  image: '',
  value: 0,
};

function AddSponsorReward({
  editingReward,
  sponsorId,
  onSuccessCreated,
  onClose,
  open,
}: GiveawayModalProps) {
  const queryClient = useQueryClient();
  const isEdit = !!editingReward;
  const [error, setError] = useState<string | null>(null);

  const [formState, setFormState] = useState<IFormState>(clearFormState);

  const formikProps = {
    initialValues: INITIAL_VALUES,
    validationSchema: FORM_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      if (isLoadingCreate || isLoadingEdit) {
        return;
      }

      if (isEdit) {
        onEdit();
      } else {
        onCreate();
      }
    },
  };

  const formik = useFormik(formikProps);

  // Create sponsor
  const { mutate: create, isPending: isLoadingCreate } = useMutation({
    mutationFn: (body: any) =>
      sponsorConroller.addSponsorDefinitions({ sponsorId, body }),
    onSuccess: (data) => {
      if (onSuccessCreated) onSuccessCreated(data);
      queryClient.invalidateQueries({
        queryKey: ['sponsor-definitions', sponsorId],
      });
      handleClose();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || 'Error adding reward';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Add reward error:', error);
    },
  });

  const { mutate: edit, isPending: isLoadingEdit } = useMutation({
    mutationFn: (body: any) =>
      sponsorConroller.updateSponsorDefinitions({
        sponsorId,
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sponsor-definitions', sponsorId],
      });
      toast.success('Reward edited successfully');
      handleClose();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || 'Error updating reward';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Edit reward error:', error);
    },
  });

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const formBody = () => {
    const { name, image, value } = formState;

    const body = {
      sponsorId: sponsorId,
      name,
      image,
      //it's necessary for the backend
      description: '<vip_rank>',
      value: Number(value),
    };

    return body;
  };

  const onCreate = () => {
    const body = formBody();

    create(body);
  };

  const onEdit = () => {
    const body = formBody();
    //@ts-ignore
    body.id = editingReward.id;

    edit(body);
  };

  const onCancel = () => {
    onClose();
    setFormState(clearFormState);
  };

  useEffect(() => {
    if (editingReward) {
      setFormState({
        name: editingReward.name || '',
        value: editingReward.value || 0,
        image: editingReward.image || '',
      });

      formik.setValues({
        name: editingReward.name || '',
        value: editingReward.value || 0,
        image: editingReward.image || '',
      });
    }
  }, [editingReward]);

  return (
    <Modal maxWidth="sm" open={open} handleClose={handleClose} isCloseIcon>
      <FormikProvider value={formik}>
        <Form>
          <div className={s.modal__header}>
            <img src={LogoImage} alt="logo" />

            <h2>
              {isEdit ? `Edit ` : `Add `}
              Reward
            </h2>
          </div>

          <div className={s.modal__form}>
            <Input
              label="Name"
              name="name"
              placeholder="Type here"
              value={formState.name}
              onChange={(e) => {
                formik.setFieldValue(e.target.name, e.target.value);
                handleChange(e);
              }}
              error={formik.errors.name}
            />

            <Input
              label="Image link"
              name="image"
              placeholder="Type here"
              value={formState.image}
              onChange={(e) => {
                formik.setFieldValue(e.target.name, e.target.value);
                handleChange(e);
              }}
              error={formik.errors.image}
            />

            <Input
              label="Value amount"
              name="value"
              type="number"
              placeholder="Type here"
              value={formState.value}
              onChange={(e) => {
                formik.setFieldValue(e.target.name, e.target.value);
                handleChange(e);
              }}
              error={formik.errors.value}
            />
          </div>

          <div className={s.modal__footer}>
            {error && <div className={s.modal__error}>{error}</div>}

            <div className={s.modal__row}>
              <Button
                onClick={onCancel}
                className={s.modal__btn}
                isOutlinedWhite
              >
                Cancel
              </Button>
              <Button className={s.modal__btn} type="submit">
                {isLoadingCreate || isLoadingEdit
                  ? 'Submitting...'
                  : isEdit
                    ? `Edit`
                    : `Add`}
              </Button>
            </div>
          </div>
        </Form>
      </FormikProvider>
    </Modal>
  );
}

export default AddSponsorReward;
